import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { acceptInvite, AuthError } from '../lib/auth';
import { useStore } from '../store';
import { AuthShell, AuthBanner, Field } from '../components/AuthShell';
import { validatePassword, PASSWORD_HINT } from '../lib/password-policy';

/**
 * Invitation acceptance landing page. The invitation token arrives as the
 * `token` query param (the workspace admin shares the link out of band). The
 * user picks a name + password; the new account is created inside the
 * inviter's workspace and the user is logged in immediately.
 */
export function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useStore((s) => s.setSession);
  const tokenParam = params.get('token') ?? '';
  const [token, setTokenField] = useState(tokenParam);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTokenField(tokenParam);
  }, [tokenParam]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!token.trim()) {
      setError('Missing invitation token.');
      return;
    }
    const pwError = validatePassword(password);
    if (pwError) {
      setError(pwError);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await acceptInvite(token.trim(), password, name.trim() || undefined);
      if (res.joined) {
        // Brand-new account → logged in, land in the app.
        setSession(res.user, res.enterprise);
        navigate('/apps');
      } else {
        // Existing account gained a membership but no session — send to sign in.
        navigate('/login', { state: { notice: res.message } });
      }
    } catch (err) {
      setError(
        err instanceof AuthError ? err.message : 'Could not accept invitation. The link may be invalid or expired.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Join workspace"
      subtitle="Set a password to finish joining the workspace that invited you."
      footer={
        <span className="muted">
          Already have an account? <Link to="/login">Sign in</Link>
        </span>
      }
    >
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form onSubmit={submit}>
        {!tokenParam && (
          <Field
            label="Invitation token"
            value={token}
            onChange={setTokenField}
            placeholder="Paste the token from your invitation"
            required
          />
        )}
        <Field label="Your name" value={name} onChange={setName} autoComplete="name" autoFocus />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          placeholder={PASSWORD_HINT}
          required
        />
        <Field
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
        />
        <button type="submit" className="btn btn--accent" disabled={busy} style={{ width: '100%', height: 38 }}>
          {busy ? 'Joining…' : 'Join workspace'}
        </button>
      </form>
    </AuthShell>
  );
}
